import {
  Component,
  inject,
  Signal,
  Pipe,
  PipeTransform,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BlogService } from '@pyg-services/blog/blog.service';
import { map, tap } from 'rxjs/operators';
import { MetaService } from '@pyg-services/meta/meta.service';
import { richTextResolver, StoryblokRichTextNode } from '@storyblok/richtext';
import { DOCUMENT, SlicePipe } from '@angular/common';
import { VideoEmbedComponent } from './video-embed/video-embed.component';
import { ShareButtonsComponent } from '@pyg-shared/share-buttons/share-buttons.component';
import { BlogItemComponent } from '../blog-item/blog-item.component';
import { Story } from '@pyg-services/blog/blog.types';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { combineLatest } from 'rxjs';

@Pipe({
  name: 'resolve',
})
export class ResolvePipe implements PipeTransform {
  private sanitizer = inject(DomSanitizer);
  transform(value: StoryblokRichTextNode<string>): SafeHtml {
    const resolver = richTextResolver();
    const resolved = resolver.render(value) as string;
    return this.sanitizer.bypassSecurityTrustHtml(resolved);
  }
}

@Component({
  selector: 'app-article',
  templateUrl: './article.component.html',
  styleUrls: ['./article.component.scss'],
  imports: [
    SlicePipe,
    VideoEmbedComponent,
    ShareButtonsComponent,
    BlogItemComponent,
    ResolvePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ArticleComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly blogService = inject(BlogService);
  private readonly metaService = inject(MetaService);
  private readonly DOC = inject(DOCUMENT);

  public article: Signal<Story | undefined>;
  public relatedData: Signal<Story[] | undefined>;

  constructor() {
    this.blogService.getAllArticles();
    const article$ = combineLatest([
      this.blogService.data$,
      this.route.paramMap,
    ]).pipe(
      map(([articles, params]) =>
        articles.find((article) => article.slug === params.get('article')),
      ),
      tap((article) => {
        const mainBlock = article?.content.components.find(
          (component) => component.component === 'main-block',
        );
        if (mainBlock?.SEO && mainBlock.image) {
          this.metaService.setMeta(
            mainBlock.SEO.title,
            mainBlock.SEO.description,
            mainBlock.image.filename,
            null,
            mainBlock.SEO.og_title,
            mainBlock.SEO.og_description,
          );
        }
      }),
    );

    this.article = toSignal(article$);

    const relatedData$ = combineLatest([
      this.blogService.data$,
      this.route.paramMap,
    ]).pipe(
      map(([articles, params]) => {
        const count = articles.length;
        const articleIndex = articles.findIndex(
          (y) => y.slug === params.get('article'),
        );

        if (articleIndex === -1) {
          return undefined;
        }

        return [
          articles[(articleIndex + count - 1) % count],
          articles[(articleIndex + 1) % count],
        ];
      }),
    );
    this.relatedData = toSignal(relatedData$);

    this.route.fragment.pipe(takeUntilDestroyed()).subscribe((fragment) => {
      if (fragment) {
        setTimeout(() => {
          this.DOC.getElementById(fragment)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        }, 400);
      }
    });
  }
}
